import React, { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { add, isAfter, isBefore, startOfDay, formatDistanceToNow, format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import classNames from 'classnames';
import Icon from '../../Icon/Icon';

import './NextDraws.scss';
import { RootState } from '../../../configs/store';
import { getFlag } from '../../../utils/utils';
import customLocale from '../../../configs/date-fns';
import BottomMenuContext from '../../BottomMenu/Context/BottomMenu.context';
import useIsMobile from 'src/hooks/useIsMobile';

interface NextDrawsProps {
  placeholder: string,
  eventType: string,
  linkTo?: string
  allowFilter?: boolean
}

function NextDraws({ placeholder, eventType, linkTo, allowFilter }: NextDrawsProps) {
  const { dataTree } = useSelector((state: RootState) => state.events);
  const { selectedTimezone } = useSelector((state: RootState) => state.odds);
  const { optionsType } = useSelector((state: RootState) => state.filters);
  const { handleOpen } = useContext(BottomMenuContext);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const mobile = useIsMobile();

  const handleToggle = () => {
    handleOpen && handleOpen('');

    //clear search bar
    dispatch.events.getSportSearchResults('');
    dispatch.events.changeSearch('');
  };

  const raceType = (event) => {
    if (event.eventType === 201 || event.eventType === 202) {
      return 'horses';
    } else if (event.eventType === 204) {
      return 'dogs';
    } else {
      return 'carriages';
    }
  };

  const renderLink = (event) => {
    const path = eventType === 'racing' ? 'racing' : 'lotto';
    const eventStart = event.eventStart * 1000;
    const date = utcToZonedTime(event.eventStart * 1000, selectedTimezone);
    const eventName = event.meetingInfos?.name ? event.meetingInfos?.name : event.name;
    let formattedDate = format(date, 'HH:mm');

    if (isBefore(eventStart, add(new Date(), { hours: 1 }))) {
      formattedDate = formatDistanceToNow(eventStart, { includeSeconds: true, locale: customLocale });
    } else if (isAfter(startOfDay(eventStart), startOfDay(new Date()))) {
      formattedDate = format(eventStart, 'MM-dd HH:mm');
    }

    return (
      <Link
        to={ `/${ path }/${ eventType === 'racing' ? event.meetingCode : event.code }/${ event.eventId }${ location.search }` }
        key={ event.eventId }
        className="next-draws__link"
        onClick={ handleToggle }
      >
        <li
          className={
            classNames({
              'next-draws__item': true,
              'next-draws__item--horse': event.eventType === 201 || event.eventType === 202,
              'next-draws__item--dog': event.eventType === 204,
              'next-draws__item--carriage': event.eventType === 203
            })
          }
        >
          <span className="next-draws__flag" style={ { backgroundImage: `url(${ getFlag(event.countryCode) })` } }></span>
          <span className="next-draws__name">{ eventName }</span>
          {
            event.productType === 2 &&
              <div className="next-draws__box">
                <span className="next-draws__competitors" title="Market count">
                  { `${ event.competitorCount }` }
                  <Icon className="next-draws__racing-icon" icon={ raceType(event) } />
                </span>
                <span className="next-draws__info--sum" title="Race number">
                  { `${ event.raceNumber }/${ event.noRaces }` }
                </span>
              </div>
          }
          <div className="next-draws__info">
            <span className="next-draws__icon--time" title="Race schedule">{ formattedDate }</span>
          </div>
        </li>
      </Link>
    );
  };

  const checkNextDrawsLink = () => {
    if (linkTo && (location.pathname !== linkTo || !mobile)){
      return <Link to={ linkTo }>{ placeholder }</Link>;
    } else if (linkTo && (location.pathname === linkTo && mobile)){
      return <span onClick={ handleToggle }>{ placeholder }</span>;
    } else {
      return <span>{ placeholder }</span>;
    }
  };

  const filteredEvents = _.filter(dataTree.nextEvents, (event) => {
    if (optionsType && allowFilter) {
      if ((event.eventType === 202 || event.eventType === 201) && optionsType.horses === true) {
        return event;
      } else if (event.eventType === 203 && optionsType.carriages === true) {
        return event;
      } else if (event.eventType === 204 && optionsType.dogs === true) {
        return event;
      } else {
        return null;
      }
    }

    return event;
  });

  return (
    <>
      <h2 className="next-draws__title">
        {
          checkNextDrawsLink()
        }
      </h2>
      <ul className="next-draws__list">
        {
          filteredEvents.length === 0 && (
            <div className="next-draws__event-not-found">{ t('events.noEvents') }</div>
          )
        }
        { filteredEvents && _.map(filteredEvents, event => renderLink(event)) }
      </ul>
    </>
  );

}

export default NextDraws;
